// src/components/CompaniesHouseDrawer.jsx
//
// Read-only company profile straight from Companies House, opened from the
// dynamic search bar for companies that are NOT one of the customer's leads.
// No enrichment / scoring / write actions live here on any plan.
import { useCallback, useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import apiService from "../services/api";
import usePlanAccess from "../hooks/usePlanAccess";
import { CAP } from "../config/planAccess";
import UpgradeLock from "./UpgradeLock";
import { statusTone } from "./CompanySearchTypeahead";
import { Drawer, DrawerContent } from "../arc/components/drawer/drawer";
import { Skeleton } from "../arc/components/skeleton/skeleton";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "-";

const titleCase = (s) =>
  (s || "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const addressLine = (a) =>
  [a?.line1, a?.line2, a?.locality, a?.region, a?.postalCode, a?.country]
    .filter(Boolean)
    .join(", ");

export const CH_DRAWER_STYLES = `
  .chd-badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 100px; font-size: 9.5px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-muted); background: var(--surface-sunken); border: 1px solid var(--border); }
  .chd-kv { display: grid; grid-template-columns: 130px 1fr; gap: 8px 12px; font-size: var(--text-sm); }
  .chd-kv dt { color: var(--text-faint); }
  .chd-kv dd { color: var(--text); overflow-wrap: anywhere; }
  .chd-list { list-style: none; }
  .chd-list li { padding: 9px 0; border-bottom: 1px solid var(--border); font-size: var(--text-sm); }
  .chd-list li:last-child { border-bottom: none; }
  .chd-sub { font-size: var(--text-xs); color: var(--text-muted); margin-top: 2px; }
  .chd-doc-link { display: inline-flex; align-items: center; gap: 4px; margin-top: 5px; font-size: var(--text-xs); font-weight: 600; color: var(--accent); text-decoration: none; }
  .chd-doc-link:hover { text-decoration: underline; }
  .chd-flag { color: var(--red); font-weight: 600; }
  .chd-muted { color: var(--text-muted); font-size: var(--text-sm); }
  .chd-stack { display: flex; flex-direction: column; gap: 12px; }
`;

export default function CompaniesHouseDrawer({
  companyNumber,
  summary,
  onClose,
  onAdded, // optional: (companyId) => void, called after a successful add
  onToast, // optional: (message, type) => void
}) {
  const { can, upgradeLabel } = usePlanAccess();
  const [state, setState] = useState("loading"); // loading | ready | error | notfound
  const [data, setData] = useState(null);
  const [showResigned, setShowResigned] = useState(false);
  const [addState, setAddState] = useState("idle"); // idle | adding | added

  const load = useCallback(async () => {
    if (!companyNumber) return;
    setState("loading");
    setData(null);
    setShowResigned(false);
    setAddState("idle");
    try {
      const res = await apiService.getCompaniesHouseCompany(companyNumber);
      setData(res.data);
      setState("ready");
    } catch (err) {
      setState(err?.response?.status === 404 ? "notfound" : "error");
    }
  }, [companyNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddToLeads = async () => {
    if (!companyNumber || addState === "adding" || addState === "added") return;
    setAddState("adding");
    try {
      const res = await apiService.addCompaniesHouseCompanyToLeads(companyNumber);
      setAddState("added");
      if (res?.data?.alreadyOwned) {
        onToast?.("Already in your leads", "info");
      } else {
        onToast?.("Added to your leads", "success");
      }
      onAdded?.(res?.data?.companyId);
    } catch (err) {
      setAddState("idle");
      if (err?.response?.data?.code === "QUOTA_EXCEEDED") {
        onToast?.(
          err.response.data.message || "Monthly lead limit reached",
          "error",
        );
      } else {
        onToast?.("Couldn't add this company to your leads", "error");
      }
    }
  };

  const open = !!companyNumber;
  const name = data?.companyName || summary?.companyName || "Company";
  const status = data?.companyStatus || summary?.companyStatus;
  const active = (data?.officers || []).filter((o) => !o.resignedOn);
  const resigned = (data?.officers || []).filter((o) => o.resignedOn);

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DrawerContent title={name} description={companyNumber} side="right">
        <div className="chd-stack">
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {status && (
              <span className={`ta-pill ${statusTone(status)}`}>
                {status.replace(/-/g, " ")}
              </span>
            )}
            <span className="chd-badge">Companies House · view-only</span>
          </div>

          {state === "loading" && (
            <Skeleton lines={6} label="Loading company details" />
          )}

              {state === "notfound" && (
                <div className="drawer-card chd-muted">
                  Companies House has no record of {companyNumber}.
                </div>
              )}

              {state === "error" && (
                <div className="drawer-card">
                  <div className="chd-muted" style={{ marginBottom: 10 }}>
                    Couldn’t reach Companies House.
                  </div>
                  <button
                    className="btn btn-accent-ghost btn-sm"
                    onClick={load}
                  >
                    Try again
                  </button>
                </div>
              )}

              {state === "ready" && data && (
                <>
                  <div className="drawer-card">
                    <div className="drawer-section-title">Business profile</div>
                    <dl className="chd-kv">
                      <dt>Type</dt>
                      <dd>{titleCase(data.companyType) || "-"}</dd>
                      <dt>Incorporated</dt>
                      <dd>{fmtDate(data.incorporatedAt)}</dd>
                      {data.dissolvedAt && (
                        <>
                          <dt>Dissolved</dt>
                          <dd>{fmtDate(data.dissolvedAt)}</dd>
                        </>
                      )}
                      <dt>Registered office</dt>
                      <dd>{addressLine(data.registeredOffice) || "-"}</dd>
                      <dt>SIC codes</dt>
                      <dd>
                        {data.sicCodes.length ? data.sicCodes.join(", ") : "-"}
                      </dd>
                      <dt>Charges</dt>
                      <dd>{data.hasCharges ? "Yes" : "None on record"}</dd>
                      <dt>Insolvency</dt>
                      <dd
                        className={data.hasInsolvencyHistory ? "chd-flag" : ""}
                      >
                        {data.hasInsolvencyHistory
                          ? "History on record"
                          : "None on record"}
                      </dd>
                    </dl>
                  </div>

                  <div className="drawer-card">
                    <div className="drawer-section-title">Filing status</div>
                    <dl className="chd-kv">
                      <dt>Accounts</dt>
                      <dd>
                        Last made up to {fmtDate(data.accounts.lastMadeUpTo)};
                        next due {fmtDate(data.accounts.nextDue)}
                        {data.accounts.overdue && (
                          <span className="chd-flag"> · Overdue</span>
                        )}
                      </dd>
                      <dt>Confirmation statement</dt>
                      <dd>
                        Last made up to{" "}
                        {fmtDate(data.confirmationStatement.lastMadeUpTo)}; next
                        due {fmtDate(data.confirmationStatement.nextDue)}
                        {data.confirmationStatement.overdue && (
                          <span className="chd-flag"> · Overdue</span>
                        )}
                      </dd>
                    </dl>
                  </div>

                  <div className="drawer-card">
                    <div className="drawer-section-title">
                      Directors &amp; officers
                    </div>
                    {data.officersUnavailable ? (
                      <div className="chd-muted">
                        Officer data is temporarily unavailable.
                      </div>
                    ) : active.length === 0 && resigned.length === 0 ? (
                      <div className="chd-muted">No officers on record.</div>
                    ) : (
                      <>
                        <ul className="chd-list">
                          {active.map((o, i) => (
                            <li key={`a${i}`}>
                              <div>{o.name}</div>
                              <div className="chd-sub">
                                {titleCase(o.role)} · appointed{" "}
                                {fmtDate(o.appointedOn)}
                              </div>
                            </li>
                          ))}
                          {showResigned &&
                            resigned.map((o, i) => (
                              <li key={`r${i}`} style={{ opacity: 0.7 }}>
                                <div>{o.name}</div>
                                <div className="chd-sub">
                                  {titleCase(o.role)} · {fmtDate(o.appointedOn)}{" "}
                                  – {fmtDate(o.resignedOn)}
                                </div>
                              </li>
                            ))}
                        </ul>
                        {resigned.length > 0 && (
                          <button
                            className="btn btn-sm"
                            style={{ marginTop: 8 }}
                            onClick={() => setShowResigned((v) => !v)}
                          >
                            {showResigned ? "Hide" : "Show"} {resigned.length}{" "}
                            resigned
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="drawer-card">
                    <div className="drawer-section-title">Company timeline</div>
                    {data.timelineUnavailable ? (
                      <div className="chd-muted">
                        Filing history is temporarily unavailable.
                      </div>
                    ) : data.timeline.length === 0 ? (
                      <div className="chd-muted">No filings on record.</div>
                    ) : (
                      <ul className="chd-list">
                        {data.timeline.map((f, i) => (
                          <li key={i}>
                            <div>{titleCase(f.description)}</div>
                            <div className="chd-sub">
                              {fmtDate(f.date)} · {titleCase(f.category)}
                            </div>
                            {f.documentUrl && (
                              <a
                                className="chd-doc-link"
                                href={f.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink size={11} /> View document (PDF)
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {!can(CAP.ENRICHMENT) && (
                    <UpgradeLock
                      feature="Enrichment, contact discovery and scoring"
                      planLabel={upgradeLabel(CAP.ENRICHMENT)}
                    />
                  )}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      className="btn btn-accent btn-sm"
                      onClick={handleAddToLeads}
                      disabled={addState === "adding" || addState === "added"}
                    >
                      {addState === "added"
                        ? "Added to your leads"
                        : addState === "adding"
                          ? "Adding…"
                          : "Add to my leads"}
                    </button>

                    <a
                      className="btn btn-sm"
                      href={`https://find-and-update.company-information.service.gov.uk/company/${data.companyNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <ExternalLink size={12} /> View on Companies House
                    </a>
                  </div>
                </>
              )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
